import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import WLoginDialog from 'login/components/w-login-dialog';
import type { ApplicationModel } from 'login/routes/application';
import { encodeBase64, fetchAssertionOptions } from 'logon-utils/logon-utils';

/** The default login page */
export default class Index extends Component<{ Args: { model: ApplicationModel } }> {
  private async getCredentials(): Promise<void> {
    const publicKeyCredentialRequestOptions = await fetchAssertionOptions();

    const credentialRequestOptions: CredentialRequestOptions = {
      publicKey: publicKeyCredentialRequestOptions,
    };

    const credential = (await navigator.credentials.get(
      credentialRequestOptions,
    )) as PublicKeyCredential | null;

    if (credential === null) {
      throw new Error('No credential retrieved');
    }

    const assertionResponse = credential.response as AuthenticatorAssertionResponse;

    const credentialId = document.getElementById('credentialId') as HTMLInputElement;

    credentialId.value = encodeBase64(credential.rawId);

    const clientDataJSON = document.getElementById('clientDataJSON') as HTMLInputElement;
    clientDataJSON.value = encodeBase64(assertionResponse.clientDataJSON);

    const authenticatorData = document.getElementById('authenticatorData') as HTMLInputElement;
    authenticatorData.value = encodeBase64(assertionResponse.authenticatorData);

    const signature = document.getElementById('signature') as HTMLInputElement;
    signature.value = encodeBase64(assertionResponse.signature);

    const clientExtensionsJSON = document.getElementById(
      'clientExtensionsJSON',
    ) as HTMLInputElement;
    clientExtensionsJSON.value = JSON.stringify(credential.getClientExtensionResults());

    const loginForm = document.getElementById('loginForm') as HTMLFormElement;
    loginForm.submit();
  }

  @action private async logonWithPasskey(): Promise<void> {
    try {
      return await this.getCredentials();
    } catch {
      // in case of an error, we do nothing
      void Promise.resolve();
    }
  }

  <template>
    <WLoginDialog
      @pageTitle={{t 'login.title.logOn'}}
      @dialogTitle={{@model.logonParams.serviceName}}
      @version={{@model.version}}
    >
      <form id='loginForm' class='form' method='post' action='login'>
        <div class='inner-form'>
          <div class='line'>
            <input
              id='emailInput'
              type='text'
              name='username'
              autocomplete='username'
              placeholder={{t 'login.emailOrLogonNameLabel'}}
              autofocus
            />
          </div>
          <div class='line'>
            <input
              id='passwordInput'
              type='password'
              name='password'
              autocomplete='current-password'
              placeholder={{t 'login.passwordLabel'}}
            />
          </div>
        </div>

        {{! hidden passkey data }}
        <input id='credentialId' name='credentialId' type='hidden' />
        <input id='clientDataJSON' name='clientDataJSON' type='hidden' />
        <input id='authenticatorData' name='authenticatorData' type='hidden' />
        <input id='signature' name='signature' type='hidden' />
        <input id='clientExtensionsJSON' name='clientExtensionsJSON' type='hidden' />

        <div class='spacer'></div>

        <div class='expanded-line'>
          <a class='submit-link' href='#/forgotPassword'>
            {{t 'login.forgotPasswordLink'}}
          </a>

          <button type='submit' class='quick-button-right'>
            {{t 'login.logOnButton'}}
          </button>
        </div>

        {{#if @model.logonParams.allowPasskeys}}
          <div class='footer'>
            <span class='submit-link'>
              {{t 'login.passkeys.explanation'}}
            </span>
            <button
              type='button'
              class='quick-button-right-no-border'
              {{on 'click' this.logonWithPasskey}}
            >
              {{t 'login.passkeys.label'}}
            </button>
          </div>
        {{/if}}
      </form>
    </WLoginDialog>
  </template>
}
